// store/appStoreV2.ts
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import type {
    FinancialDetailsRequestV2,
    OfferValidation,
    PaymentPlan,
    PersonalDetailsClientDTO,
    PreQualifiedInfoDTO,
} from '@leanpay/models'

interface AppState {
    isAuthenticated: boolean
    vendorToken: string
    vendorData: PreQualifiedInfoDTO | null
    phoneNumber: string | null
    progress: number
    offerValidationData: OfferValidation | null
    personalDetailsData: PersonalDetailsClientDTO | null
    selectedPaymentPlan: PaymentPlan | null
    financialDetailsDataV2: FinancialDetailsRequestV2 | null
    isCookieConsentEnabled: boolean
    lastRecordedRoute: string

    setIsAuthenticated: (value: boolean) => void
    setVendorToken: (token: string) => void
    setVendorData: (vendor: PreQualifiedInfoDTO | null) => void
    setPhoneNumber: (phone: string | null) => void
    setProgress: (progress: number) => void
    setOfferValidationData: (data: OfferValidation | null) => void
    setPersonalDetailsData: (data: PersonalDetailsClientDTO | null) => void
    setSelectedPaymentPlan: (data: PaymentPlan | null) => void
    setFinancialDetailsDataV2: (data: FinancialDetailsRequestV2 | null) => void
    setIsCookieConsentEnabled: (value: boolean) => void
    setLastRecordedRoute: (route: string) => void
    canAccessRoute: (route: string) => boolean
    resetStore: () => void
}

export const useAppStore = create<AppState>()(
    persist(
        (set, get) => ({
            isAuthenticated: false,
            vendorToken: '',
            vendorData: null,
            phoneNumber: null,
            progress: 0,
            offerValidationData: null,
            personalDetailsData: null,
            selectedPaymentPlan: null,
            financialDetailsDataV2: null,
            isCookieConsentEnabled: false,
            lastRecordedRoute: '/',

            setIsAuthenticated: (value) => set({ isAuthenticated: value }),
            setVendorToken: (token) => set({ vendorToken: token }),
            setVendorData: (vendor) => set({ vendorData: vendor }),
            setPhoneNumber: (phone) => set({ phoneNumber: phone }),
            setProgress: (progress) => set({ progress }),
            setOfferValidationData: (data) => set({ offerValidationData: data }),
            setPersonalDetailsData: (data) => set({ personalDetailsData: data }),
            setSelectedPaymentPlan: (data) => set({ selectedPaymentPlan: data }),
            setFinancialDetailsDataV2: (data) => set({ financialDetailsDataV2: data }),
            setIsCookieConsentEnabled: (value) => set({ isCookieConsentEnabled: value }),
            setLastRecordedRoute: (route) => set({ lastRecordedRoute: route }),
            canAccessRoute: (route) => {
                const { lastRecordedRoute } = get()
                // User can only access their last recorded route (for refresh)
                return route === lastRecordedRoute
            },
            resetStore: () =>
                set({
                    isAuthenticated: false,
                    vendorToken: '',
                    vendorData: null,
                    phoneNumber: null,
                    progress: 0,
                    offerValidationData: null,
                    personalDetailsData: null,
                    selectedPaymentPlan: null,
                    financialDetailsDataV2: null,
                    isCookieConsentEnabled: false,
                    lastRecordedRoute: '/',
                }),
        }),
        {
            name: 'leanpay-prequalify-session',
            storage: createJSONStorage(() => sessionStorage),
        },
    ),
)
